import React, {useCallback, useState} from 'react';
import {
  BaseDropTargetLayoutProps,
  DragableItem,
  DropTargetOffsets,
  ItemWithType,
  Position,
  Rect,
} from '../types';
import {useBaseLayoutProps} from '../hooks';
import {DropTargetMonitor, useDrop} from 'react-dnd';
import assert from 'assert-ts';

type DropTargetState = 'none' | 'hovering';

const getRelativePosition = (
  position: Position,
  relativeTo: Position,
): Position => ({
  x: position.x - relativeTo.x,
  y: position.y - relativeTo.y,
});

export const BaseDropTargetLayout = function <TItem extends DragableItem>(
  props: React.PropsWithChildren<BaseDropTargetLayoutProps<TItem>>,
) {
  const {
    type,
    disabled,
    enabled = true,
    onPress,
    onBlur,
    onBeginHover,
    onEndHover,
    onHover,
    onDrop,
    children,
  } = props;
  const {classNameProp, handleClick} = useBaseLayoutProps(props);
  const [targetState, setTargetState] = useState<DropTargetState>('none');
  const [targetRef, setTargetRef] = useState<
    HTMLButtonElement | HTMLDivElement | null
  >(null);
  const [targetRect, setTargetRect] = useState<Rect>();

  const [, drop] = useDrop<
    ItemWithType<TItem>,
    ItemWithType<TItem>,
    undefined
  >(() => {
    const getOffsets = (
      monitor: DropTargetMonitor,
      rect?: Rect,
    ): DropTargetOffsets => ({
      target: assert(
        rect ?? targetRect,
        'BaseDropTargetLayout.getOffsets: targetRect',
      ),
      cursorRelative: getRelativePosition(
        assert(
          monitor.getClientOffset(),
          'BaseDropTargetLayout.getOffsets: monitor.getClientOffset()',
        ),
        assert(
          rect ?? targetRect,
          'BaseDropTargetLayout.getOffsets: targetRect',
        ),
      ),
      dragableRelative: getRelativePosition(
        assert(
          monitor.getSourceClientOffset(),
          'BaseDropTargetLayout.getOffsets: monitor.getSourceClientOffset()',
        ),
        assert(
          rect ?? targetRect,
          'BaseDropTargetLayout.getOffsets: targetRect',
        ),
      ),
    });
    return {
      accept: type,
      hover: (item, monitor) => {
        if (onHover && targetState === 'hovering') {
          onHover(item, getOffsets(monitor));
          // debouncedLog(
          //   'BaseDropTargetLayout: hover:',
          //   type,
          //   ' :',
          //   JSON.stringify(offsets),
          // );
        }
      },
      drop: (item, monitor) => {
        const offsets = getOffsets(monitor);
        // console.log(
        //   'BaseDropTargetLayout: drop:',
        //   type,
        //   ' :',
        //   JSON.stringify(item),
        //   ' :',
        //   JSON.stringify(offsets),
        // );
        onDrop && onDrop(item, offsets);
        return item;
      },
      collect: monitor => {
        const isOver = !!monitor.isOver();
        if (!isOver && targetState === 'none') {
          // debouncedLog('BaseDropTargetLayout: none');
          return undefined;
        }

        const item = monitor.getItem() as ItemWithType<TItem>;

        if (targetState === 'none') {
          const rect = assert(
            targetRef,
            'BaseDropTargetLayout.collect: targetRef',
          ).getBoundingClientRect();
          setTargetRect(rect);
          const offsets = getOffsets(monitor, rect);
          // console.log(
          //   'BaseDropTargetLayout: beginHover:',
          //   type,
          //   ' :',
          //   JSON.stringify(offsets),
          // );
          setTargetState('hovering');
          onBeginHover && onBeginHover(item, offsets);
        } else {
          // console.log('BaseDropTargetLayout: endHover:', type);
          setTargetState('none');
          onEndHover && onEndHover(item);
        }

        return undefined;
      },
    };
  }, [targetRef, targetState]);

  const setRef = useCallback(
    (element: any) => {
      drop(element);
      setTargetRef(element as HTMLButtonElement | HTMLDivElement);
    },
    [drop],
  );

  return onPress ? (
    <button
      ref={enabled ? setRef : undefined}
      {...classNameProp}
      disabled={disabled}
      onClick={handleClick}
      onBlur={onBlur}>
      {children}
    </button>
  ) : (
    <div ref={enabled ? setRef : undefined} {...classNameProp} onBlur={onBlur}>
      {children}
    </div>
  );
};
